import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import type { WhatsAppTemplatePresentation } from "@multi-chat/shared";
import {
  ExternalLink as ExternalLinkIcon,
  List as ListIcon,
  Phone as PhoneIcon,
  PhoneCall as PhoneCallIcon,
  Reply as ReplyIcon,
} from "lucide-react";
import { LinkifiedText } from "../../../components/common/LinkifiedText/index.js";
import { cn } from "../../../lib/utils.js";
import {
  PREVIEW_VISIBLE_BUTTON_COUNT,
  previewShouldCollapseButtons,
  previewVisibleButtonsWhenCollapsed,
} from "../template-editor-presentation.js";
import { templateContentDir } from "./template-content-dir.js";

export interface WhatsAppTemplatePreviewProps {
  presentation: WhatsAppTemplatePresentation;
  /** Optional BCP-47-ish hint for content direction (e.g. he, en_US). */
  contentLanguage?: string | null;
  className?: string;
  /** Compact bubble mode for conversation history. */
  compact?: boolean;
  /**
   * When true (default), 4+ buttons collapse to the first two plus a "See all"
   * row. Pass false only when a surface must show every button at once.
   */
  collapseWhenMany?: boolean;
  seeAllLabel?: string;
  imagePlaceholderLabel?: string;
  /**
   * Optional replacement nodes for header/body/footer text.
   * Omitted slots keep LinkifiedText and the existing classes.
   */
  textSlots?: {
    header?: ReactNode;
    body?: ReactNode;
    footer?: ReactNode;
  };
  /**
   * Optional extra node rendered inside a button row, keyed by the button's
   * original index. Omitted: button rows render exactly as before.
   */
  renderButtonAccessory?: (buttonIndex: number) => ReactNode;
}

type PreviewButton = WhatsAppTemplatePresentation["buttons"][number];

function PreviewButtonInner({
  button,
}: {
  button: PreviewButton;
}): React.JSX.Element {
  switch (button.type) {
    case "QUICK_REPLY":
      return (
        <>
          <ReplyIcon size={14} aria-hidden />
          <span>{button.text}</span>
        </>
      );
    case "URL":
      return (
        <>
          <ExternalLinkIcon size={14} aria-hidden />
          <span>{button.text}</span>
        </>
      );
    case "PHONE_NUMBER":
      return (
        <>
          <PhoneIcon size={14} aria-hidden />
          <span>{button.text}</span>
        </>
      );
    case "VOICE_CALL":
      return (
        <>
          <PhoneCallIcon size={14} aria-hidden />
          <span>{button.text}</span>
        </>
      );
    default: {
      const _exhaustive: never = button;
      return _exhaustive;
    }
  }
}

function previewButtonClass(compact: boolean): string {
  return cn(
    "flex flex-wrap items-center justify-center gap-x-2 gap-y-1 font-semibold text-accent last:border-b-0 last:pb-0",
    compact
      ? "border-b border-black/8 py-1.5 text-[0.8125rem] last:pb-0"
      : "border-b border-[color-mix(in_srgb,var(--color-border)_70%,transparent)] py-[0.55rem] text-[0.88rem] text-[color-mix(in_srgb,var(--color-accent)_85%,var(--color-foreground))]",
  );
}

function PreviewButtonRow({
  button,
  compact,
  accessory,
}: {
  button: PreviewButton;
  compact: boolean;
  accessory?: ReactNode;
}): React.JSX.Element {
  return (
    <li className={previewButtonClass(compact)}>
      <PreviewButtonInner button={button} />
      {accessory}
    </li>
  );
}

function PreviewButtonList({
  buttons,
  compact,
  collapseWhenMany,
  seeAllLabel,
  renderButtonAccessory,
}: {
  buttons: readonly PreviewButton[];
  compact: boolean;
  collapseWhenMany: boolean;
  seeAllLabel: string;
  renderButtonAccessory?: (buttonIndex: number) => ReactNode;
}): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const canCollapse = collapseWhenMany && previewShouldCollapseButtons(buttons.length);
  const leading = canCollapse
    ? previewVisibleButtonsWhenCollapsed(buttons)
    : buttons;
  const overflow = canCollapse ? buttons.slice(PREVIEW_VISIBLE_BUTTON_COUNT) : [];
  const showOverflow = canCollapse && expanded;

  return (
    <ul
      className={cn(
        "m-0 grid list-none gap-0 p-0",
        compact
          ? "mt-0.5 border-t border-black/10"
          : "border-t border-[color-mix(in_srgb,var(--color-border)_80%,transparent)]",
      )}
      aria-label="Template buttons"
    >
      {leading.map((button, index) => (
        <PreviewButtonRow
          key={`${button.type}-${button.text}-${index}`}
          button={button}
          compact={compact}
          accessory={renderButtonAccessory?.(index)}
        />
      ))}
      <AnimatePresence initial={false}>
        {showOverflow
          ? overflow.map((button, index) => (
              <motion.li
                key={`${button.type}-${button.text}-extra-${index}`}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className={cn("overflow-hidden", previewButtonClass(compact))}
              >
                <PreviewButtonInner button={button} />
                {renderButtonAccessory?.(PREVIEW_VISIBLE_BUTTON_COUNT + index)}
              </motion.li>
            ))
          : null}
      </AnimatePresence>
      {canCollapse ? (
        <li className="last:border-b-0">
          <button
            type="button"
            className={cn(
              "flex w-full items-center justify-center gap-2 border-0 bg-transparent font-semibold text-accent",
              compact ? "py-1.5 text-[0.8125rem]" : "py-[0.55rem] text-[0.88rem]",
            )}
            onClick={() => setExpanded((current) => !current)}
            aria-expanded={expanded}
          >
            <ListIcon size={14} aria-hidden />
            {seeAllLabel}
          </button>
        </li>
      ) : null}
    </ul>
  );
}

export function WhatsAppTemplatePreview({
  presentation,
  contentLanguage,
  className,
  compact = false,
  collapseWhenMany = true,
  seeAllLabel,
  imagePlaceholderLabel = "Image",
  textSlots,
  renderButtonAccessory,
}: WhatsAppTemplatePreviewProps): React.JSX.Element {
  const { t } = useTranslation();
  const dir = templateContentDir(contentLanguage);
  const resolvedSeeAllLabel =
    seeAllLabel ?? t("whatsapp.templates.manage.wizard.editor.previewSeeAll");

  return (
    <div
      className={cn("grid justify-items-stretch", compact && "w-full", className)}
      dir={dir}
      data-testid="whatsapp-template-preview"
    >
      <div
        className={cn(
          "grid max-w-full gap-1.5 wrap-anywhere",
          compact
            ? "rounded-none border-0 bg-transparent p-0 shadow-none"
            : "rounded-md border border-[color-mix(in_srgb,var(--color-border)_70%,transparent)] bg-[color-mix(in_srgb,var(--color-surface)_92%,#dcf8c6)] p-3 shadow-[0_1px_2px_color-mix(in_srgb,#000_8%,transparent)]",
        )}
      >
        {presentation.header?.type === "IMAGE" ? (
          presentation.header.imageUrl && !presentation.header.missing ? (
            <div
              className={cn(
                "overflow-hidden bg-black/5",
                compact ? "-mx-0 rounded-sm" : "-mx-3 -mt-3 rounded-t-[0.4rem]",
              )}
            >
              <img
                src={presentation.header.imageUrl}
                alt=""
                className="block max-h-64 w-full object-contain"
              />
            </div>
          ) : (
            <div
              className={cn(
                "grid min-h-24 place-items-center bg-[color-mix(in_srgb,var(--color-surface-muted)_80%,transparent)] text-ink-muted",
                compact ? "rounded-sm" : "-mx-3 -mt-3 rounded-t-[0.4rem] py-6",
              )}
              data-testid="whatsapp-template-image-placeholder"
            >
              <span className="text-[0.8rem] font-medium">
                {presentation.header.fileName ?? imagePlaceholderLabel}
              </span>
            </div>
          )
        ) : presentation.header?.type === "TEXT" ? (
          <p
            className={cn(
              "m-0 font-semibold text-ink",
              compact
                ? "text-[0.875rem] font-medium leading-[1.4]"
                : "text-[0.95rem] leading-[1.35] font-bold",
            )}
          >
            {textSlots?.header ?? <LinkifiedText text={presentation.header.text} />}
          </p>
        ) : null}
        <p
          className={cn(
            "m-0 whitespace-pre-wrap text-ink",
            compact
              ? "text-[0.875rem] font-normal leading-[1.45]"
              : "text-[0.92rem] leading-[1.45]",
          )}
        >
          {textSlots?.body ?? <LinkifiedText text={presentation.body.text} />}
        </p>
        {presentation.footer ? (
          <p className="m-0 text-[0.75rem] font-normal leading-[1.35] text-ink-muted">
            {textSlots?.footer ?? <LinkifiedText text={presentation.footer.text} />}
          </p>
        ) : null}
        {presentation.buttons.length > 0 ? (
          <PreviewButtonList
            buttons={presentation.buttons}
            compact={compact}
            collapseWhenMany={collapseWhenMany}
            seeAllLabel={resolvedSeeAllLabel}
            renderButtonAccessory={renderButtonAccessory}
          />
        ) : null}
      </div>
    </div>
  );
}
